import React from 'react'
import {
  Paragraph,
  Button,
  H3,
  WhiteTheme,
  Separator,
  DialogOverlay,
  DialogOverlayActions,
  useOverlay,
} from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { ControlReset, NoticeLoading, TreeFan } from '@fs/zion-icon'
import { useMemoryBook } from '../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../helpers/getHasDarkBackground'
import useFanChartPDF from '../../hooks/useFanChartPDF/useFanChartPDF'

const containerCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 95%;
  width: 100%;
`

const printTitleCss = css`
  margin-bottom: 3em;
`

const innerCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const imageCss = (isPrintView) => css`
  object-fit: contain;

  ${isPrintView
    ? `
      width: 100%;
      max-height: 100%;
    `
    : `
      flex: 1;
      min-height: 0;
      width: 100%;
    `}
`

function FanChartImageContent({ isPrintView, fanChartImageUrl, isOnlyPerson, refetch, personId }) {
  const [t] = useTranslation()
  const fanChartLinkOverlay = useOverlay()

  if (isPrintView) {
    return (
      <>
        <div css={printTitleCss}>
          <H3 centered>{t('FanChartPage.title', 'Fan Chart')}</H3>
        </div>
        <img src={fanChartImageUrl} alt={t('FanChartPage.imageAlt', 'Family Fan Chart')} css={imageCss(true)} />
      </>
    )
  }

  return (
    <>
      <H3 centered>{t('FanChartPage.title', 'Fan Chart')}</H3>
      <Button Icon={ControlReset} label={t('FanChartPage.refresh', 'Refresh Fan Chart')} onClick={refetch}>
        {t('FanChartPage.refresh', 'Refresh Fan Chart')}
      </Button>
      <Separator size="sm" />
      <img src={fanChartImageUrl} alt={t('FanChartPage.imageAlt', 'Family Fan Chart')} css={imageCss(false)} />
      <Separator size="md" />
      <Paragraph size="sm" centered>
        {isOnlyPerson
          ? t(
              'FanChartPage.descriptionOnlyPerson',
              'Visit the Fan Chart Tree View (a fan-shaped family tree display) to create a beautiful family fan chart.'
            )
          : t(
              'FanChartPage.description',
              'Go to the Fan Chart Tree View (a fan-shaped family tree display) to continue adding family members. The fan chart displays up to seven generations.'
            )}
      </Paragraph>
      <Separator size="xs" />
      <Button Icon={TreeFan} emphasis="medium" onClick={fanChartLinkOverlay.open}>
        {t('FanChartPage.button.toTreeView', 'Fan Chart Tree View')}
      </Button>
      <DialogOverlay
        title={t('FanChartPage.fanChartDialog.title', 'Going to Fan Chart Tree View')}
        size="sm"
        {...fanChartLinkOverlay}
      >
        <Paragraph size="sm">
          {t(
            'FanChartPage.fanChartDialog.body1',
            "You're leaving the Memory Book to add or edit members in the Fan Chart Tree View."
          )}
        </Paragraph>
        <Separator size="sm" />
        <Paragraph size="sm">
          {t(
            'FanChartPage.fanChartDialog.body2',
            'When you\'re finished, return to the Memory Book and select "REFRESH FAN CHART" to see your updates.'
          )}
        </Paragraph>
        <Separator size="sm" />
        <DialogOverlayActions>
          <Button onClick={fanChartLinkOverlay.close}>{t('common-ui:cancel.action', 'Cancel')}</Button>
          <Button
            emphasis="high"
            to={`/tree/pedigree/fanchart/${personId}`}
            external
            target="_blank"
            onClick={fanChartLinkOverlay.close}
            linkName="Button:Redirect to Fan Chart:Continue"
          >
            {t('common-ui:continue.action', 'Continue')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlay>
    </>
  )
}

function FanChartPage({ pageNumber, isPrintView = false }) {
  const [t] = useTranslation()
  const { personId } = useUser()
  const { memoryBook } = useMemoryBook()
  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)

  const { fanChartImageUrl, isLoading, error, refetch, isOnlyPerson } = useFanChartPDF(personId)

  const printLoadingAttr = isLoading ? 'true' : undefined

  // Success state - check before error since a loaded image takes priority over stale error state
  if (fanChartImageUrl) {
    const imageContent = (
      <div css={innerCss}>
        <FanChartImageContent
          isPrintView={isPrintView}
          fanChartImageUrl={fanChartImageUrl}
          isOnlyPerson={isOnlyPerson}
          refetch={refetch}
          personId={personId}
        />
      </div>
    )
    return <div css={containerCss}>{hasDarkBackground ? <WhiteTheme>{imageContent}</WhiteTheme> : imageContent}</div>
  }

  // Loading state
  if (isLoading) {
    const loadingContent = (
      <div css={innerCss}>
        {isPrintView ? (
          <H3 centered>{t('FanChartPage.title', 'Fan Chart')}</H3>
        ) : (
          <>
            <H3 centered>{t('FanChartPage.title', 'Fan Chart')}</H3>
            <Separator size="lg" />
            <NoticeLoading size="lg" />
          </>
        )}
      </div>
    )

    return (
      <div css={containerCss} data-print-loading={printLoadingAttr}>
        {hasDarkBackground ? <WhiteTheme>{loadingContent}</WhiteTheme> : loadingContent}
      </div>
    )
  }

  // Error state
  if (error) {
    const errorContent = (
      <div css={innerCss}>
        {isPrintView ? (
          <H3 centered>{t('FanChartPage.title', 'Fan Chart')}</H3>
        ) : (
          <>
            <H3 centered>{t('FanChartPage.title', 'Fan Chart')}</H3>
            <Separator size="lg" />
            <Paragraph centered secondary>
              {t('FanChartPage.error', 'Error loading fan chart. Please try again.')}
            </Paragraph>
            <Separator size="sm" />
            <Button emphasis="medium" onClick={refetch}>
              {t('FanChartPage.retry', 'Retry')}
            </Button>
          </>
        )}
      </div>
    )

    return (
      <div css={containerCss} data-print-loading={printLoadingAttr}>
        {hasDarkBackground ? <WhiteTheme>{errorContent}</WhiteTheme> : errorContent}
      </div>
    )
  }

  // Fallback - should not reach here normally
  return (
    <div css={containerCss} data-print-loading={printLoadingAttr}>
      <div css={innerCss}>
        <H3 centered>{t('FanChartPage.title', 'Fan Chart')}</H3>
      </div>
    </div>
  )
}

export default FanChartPage
